import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Mic, MicOff, Play, Pause, Square, Clock, Users, FileText,
  CheckCircle, AlertCircle, ChevronRight, ChevronDown, Plus,
  Download, Send, RefreshCw, Timer, UserCheck, Calendar,
  Brain, Activity, Pill, Heart, MessageSquare, Edit, Save, X
} from 'lucide-react';

// Patient Card for IDG Review
const PatientReviewCard = ({ patient, isActive, onSelect, onComplete, isCompleted }) => {
  const priorityColors = {
    high: 'border-red-500 bg-red-50',
    medium: 'border-yellow-500 bg-yellow-50',
    low: 'border-green-500 bg-green-50'
  };
  
  return (
    <div 
      className={`p-4 border-l-4 rounded-lg cursor-pointer transition-all ${
        priorityColors[patient.priority] || 'border-gray-300 bg-white'
      } ${isActive ? 'ring-2 ring-blue-500' : ''} ${isCompleted ? 'opacity-60' : ''}`}
      onClick={() => !isCompleted && onSelect(patient)}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold">{patient.name}</h4>
            {isCompleted && <CheckCircle className="w-4 h-4 text-green-500" />}
          </div>
          <p className="text-sm text-muted-foreground">{patient.diagnosis}</p>
          <div className="flex flex-wrap gap-1 mt-2">
            {patient.discussionPoints?.slice(0, 2).map((point, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {point}
              </Badge>
            ))}
          </div>
        </div>
        <div className="text-right">
          <Badge className={
            patient.priority === 'high' ? 'bg-red-500' :
            patient.priority === 'medium' ? 'bg-yellow-500' :
            'bg-green-500'
          }>
            {patient.priority}
          </Badge>
          {patient.painScore !== undefined && (
            <p className="text-sm mt-1">Pain: {patient.painScore}/10</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Action Item Component
const ActionItem = ({ item, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(item.text);
  
  return (
    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
      <Checkbox 
        checked={item.completed}
        onCheckedChange={(checked) => onUpdate({ ...item, completed: checked })}
      />
      <div className="flex-1">
        {editing ? (
          <div className="flex gap-2">
            <Input 
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="flex-1"
            />
            <Button size="sm" onClick={() => { onUpdate({ ...item, text: editText }); setEditing(false); }}>
              <Save className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <div className="flex items-start justify-between">
            <div>
              <p className={item.completed ? 'line-through text-muted-foreground' : ''}>{item.text}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Assigned to: {item.assignee} | Due: {item.dueDate}
              </p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
              <Edit className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Main IDG Live Meeting Component
export default function IDGLiveMeeting() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meetingStatus, setMeetingStatus] = useState('preparing'); // preparing, in_progress, paused, completed
  const [meetingTimer, setMeetingTimer] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const timerRef = useRef(null);
  
  // Meeting data
  const [attendees, setAttendees] = useState([
    { id: 1, name: 'Dr. Sarah Johnson', role: 'Medical Director', present: true },
    { id: 2, name: 'Maria Garcia, RN', role: 'RN Case Manager', present: true },
    { id: 3, name: 'James Wilson, MSW', role: 'Social Worker', present: true },
    { id: 4, name: 'Rev. Emily Brown', role: 'Chaplain', present: false },
    { id: 5, name: 'Lisa Chen', role: 'Volunteer Coordinator', present: true }
  ]);
  
  const [patients, setPatients] = useState([
    {
      id: 'P002',
      name: 'Mary Johnson',
      diagnosis: 'Metastatic Breast Cancer',
      priority: 'high',
      painScore: 7,
      discussionPoints: ['Pain increase', 'Medication adjustment', 'Family meeting'],
      notes: '',
      decisions: [],
      reviewed: false
    },
    {
      id: 'P001',
      name: 'John Smith',
      diagnosis: 'End-stage COPD',
      priority: 'medium',
      painScore: 3,
      discussionPoints: ['Recertification', 'Oxygen needs', 'Caregiver support'],
      notes: '',
      decisions: [],
      reviewed: false
    },
    {
      id: 'P003',
      name: 'Robert Davis',
      diagnosis: 'CHF Stage D',
      priority: 'low',
      painScore: 2,
      discussionPoints: ['Stable - quick review'],
      notes: '',
      decisions: [],
      reviewed: false
    }
  ]);
  
  const [activePatient, setActivePatient] = useState(null);
  const [actionItems, setActionItems] = useState([]);
  const [meetingNotes, setMeetingNotes] = useState('');
  const [newActionItem, setNewActionItem] = useState({ text: '', assignee: '', dueDate: '' });
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [loadingAI, setLoadingAI] = useState(false);

  // Timer effect
  useEffect(() => {
    if (meetingStatus === 'in_progress') {
      timerRef.current = setInterval(() => {
        setMeetingTimer(t => t + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [meetingStatus]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startMeeting = () => {
    setMeetingStatus('in_progress');
    toast.success('IDG Meeting started. Recording in progress.');
  };

  const pauseMeeting = () => {
    setMeetingStatus('paused');
    toast.info('Meeting paused');
  };

  const resumeMeeting = () => {
    setMeetingStatus('in_progress');
    toast.info('Meeting resumed');
  };

  const endMeeting = () => {
    setMeetingStatus('completed');
    toast.success('Meeting completed. Generating documentation...');
    // In real implementation, would generate and save meeting minutes
  };

  const loadAIRecommendations = async (patientId) => {
    setLoadingAI(true);
    try {
      const response = await api.post(`/care-plan-ai/generate/${patientId}`);
      setAiRecommendations(response.data.recommendations || []);
    } catch (error) {
      console.error('AI recommendations error:', error);
      // Mock recommendations
      setAiRecommendations([
        {
          recommendation_id: '1',
          category: 'medication',
          priority: 'urgent',
          title: 'Consider increasing pain medication',
          description: 'Patient shows upward pain trend. Current regimen may be insufficient.',
          suggested_actions: ['Increase fentanyl patch to 75mcg', 'Add breakthrough medication']
        },
        {
          recommendation_id: '2',
          category: 'assessment',
          priority: 'high',
          title: 'Schedule psychosocial assessment',
          description: 'Anxiety trending upward alongside pain increase.',
          suggested_actions: ['Social worker visit within 48 hours', 'Screen for depression']
        }
      ]);
    } finally {
      setLoadingAI(false);
    }
  };

  const selectPatient = (patient) => {
    setActivePatient(patient);
    loadAIRecommendations(patient.id);
  };

  const markPatientReviewed = (patientId) => {
    setPatients(prev => prev.map(p => 
      p.id === patientId ? { ...p, reviewed: true } : p
    ));
    toast.success('Patient review completed');
  };

  const addActionItem = () => {
    if (!newActionItem.text) return;
    
    setActionItems(prev => [...prev, {
      id: Date.now(),
      ...newActionItem,
      patientId: activePatient?.id,
      patientName: activePatient?.name,
      completed: false,
      createdAt: new Date().toISOString()
    }]);
    
    setNewActionItem({ text: '', assignee: '', dueDate: '' });
    toast.success('Action item added');
  };

  const updatePatientNotes = (notes) => {
    if (!activePatient) return;
    setPatients(prev => prev.map(p =>
      p.id === activePatient.id ? { ...p, notes } : p
    ));
  };

  const addDecision = (decision) => {
    if (!activePatient) return;
    setPatients(prev => prev.map(p =>
      p.id === activePatient.id ? { ...p, decisions: [...p.decisions, decision] } : p
    ));
  };

  const acceptRecommendation = (rec) => {
    addDecision(`Accepted: ${rec.title}`);
    rec.suggested_actions.forEach(action => {
      setActionItems(prev => [...prev, {
        id: Date.now() + Math.random(),
        text: action,
        assignee: 'RN Case Manager',
        dueDate: new Date(Date.now() + 48 * 60 * 60 * 1000).toLocaleDateString(),
        patientId: activePatient?.id,
        patientName: activePatient?.name,
        completed: false,
        createdAt: new Date().toISOString(),
        fromAI: true
      }]);
    });
    toast.success('Recommendation accepted and action items created');
  };

  const patientsReviewed = patients.filter(p => p.reviewed).length;
  const progressPercent = Math.round((patientsReviewed / patients.length) * 100);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="idg-live-meeting">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 to-blue-600 text-white p-4 shadow-lg sticky top-0 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-xl font-bold">IDG Meeting - Live Session</h1>
                <p className="text-blue-100 text-sm">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
              
              {/* Timer */}
              <div className="bg-white/20 px-4 py-2 rounded-lg flex items-center gap-2">
                <Timer className="w-5 h-5" />
                <span className="font-mono text-lg">{formatTime(meetingTimer)}</span>
              </div>
              
              {/* Status Badge */}
              <Badge className={
                meetingStatus === 'in_progress' ? 'bg-green-500' :
                meetingStatus === 'paused' ? 'bg-yellow-500' :
                meetingStatus === 'completed' ? 'bg-blue-500' :
                'bg-gray-500'
              }>
                {meetingStatus === 'in_progress' ? '● Recording' :
                 meetingStatus === 'paused' ? '⏸ Paused' :
                 meetingStatus === 'completed' ? '✓ Completed' :
                 'Ready to Start'}
              </Badge>
            </div>
            
            {/* Meeting Controls */}
            <div className="flex items-center gap-2">
              {meetingStatus === 'preparing' && (
                <Button onClick={startMeeting} className="bg-green-600 hover:bg-green-700">
                  <Play className="w-4 h-4 mr-2" />
                  Start Meeting
                </Button>
              )}
              {meetingStatus === 'in_progress' && (
                <>
                  <Button onClick={pauseMeeting} variant="secondary">
                    <Pause className="w-4 h-4 mr-2" />
                    Pause
                  </Button>
                  <Button onClick={endMeeting} className="bg-red-600 hover:bg-red-700">
                    <Square className="w-4 h-4 mr-2" />
                    End Meeting
                  </Button>
                </>
              )}
              {meetingStatus === 'paused' && (
                <>
                  <Button onClick={resumeMeeting} className="bg-green-600 hover:bg-green-700">
                    <Play className="w-4 h-4 mr-2" />
                    Resume
                  </Button>
                  <Button onClick={endMeeting} className="bg-red-600 hover:bg-red-700">
                    <Square className="w-4 h-4 mr-2" />
                    End Meeting
                  </Button>
                </>
              )}
              {meetingStatus === 'completed' && (
                <Button onClick={() => navigate('/hospice-ehr')} variant="secondary">
                  <FileText className="w-4 h-4 mr-2" />
                  View Minutes
                </Button>
              )}
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm mb-1">
              <span>Patients Reviewed: {patientsReviewed}/{patients.length}</span>
              <span>{progressPercent}%</span>
            </div>
            <div className="w-full bg-blue-900 rounded-full h-2">
              <div 
                className="bg-green-400 h-2 rounded-full transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">
        <div className="grid grid-cols-12 gap-4">
          {/* Left Sidebar - Attendees & Patient Queue */}
          <div className="col-span-3 space-y-4">
            {/* Attendees */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Attendees ({attendees.filter(a => a.present).length}/{attendees.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="py-0 pb-3">
                <div className="space-y-2">
                  {attendees.map(attendee => (
                    <div 
                      key={attendee.id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <Checkbox 
                        checked={attendee.present}
                        onCheckedChange={(checked) => {
                          setAttendees(prev => prev.map(a =>
                            a.id === attendee.id ? { ...a, present: checked } : a
                          ));
                        }}
                      />
                      <div className={attendee.present ? '' : 'text-muted-foreground line-through'}>
                        <p className="font-medium">{attendee.name}</p>
                        <p className="text-xs text-muted-foreground">{attendee.role}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Patient Queue */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Patient Queue</CardTitle>
              </CardHeader>
              <CardContent className="py-0 pb-3 space-y-2">
                {patients.map(patient => (
                  <PatientReviewCard
                    key={patient.id}
                    patient={patient}
                    isActive={activePatient?.id === patient.id}
                    isCompleted={patient.reviewed}
                    onSelect={selectPatient}
                    onComplete={markPatientReviewed}
                  />
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Main Content - Active Patient Review */}
          <div className="col-span-6 space-y-4">
            {activePatient ? (
              <>
                {/* Patient Header */}
                <Card>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-xl font-bold">{activePatient.name}</h2>
                        <p className="text-muted-foreground">{activePatient.diagnosis}</p>
                        <div className="flex gap-2 mt-2">
                          <Badge>Pain: {activePatient.painScore}/10</Badge>
                          <Badge variant="outline">Level of Care: Routine</Badge>
                        </div>
                      </div>
                      <Button 
                        onClick={() => markPatientReviewed(activePatient.id)}
                        disabled={activePatient.reviewed}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {activePatient.reviewed ? 'Reviewed' : 'Mark Reviewed'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Discussion Points */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm">Discussion Points</CardTitle>
                  </CardHeader>
                  <CardContent className="py-0 pb-3">
                    <ul className="space-y-2">
                      {activePatient.discussionPoints.map((point, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <ChevronRight className="w-4 h-4 text-blue-500" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                {/* AI Recommendations */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Brain className="w-4 h-4 text-purple-600" />
                      AI Care Plan Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="py-0 pb-3">
                    {loadingAI ? (
                      <div className="flex items-center justify-center py-4">
                        <RefreshCw className="w-5 h-5 animate-spin text-purple-600" />
                      </div>
                    ) : aiRecommendations.length > 0 ? (
                      <div className="space-y-3">
                        {aiRecommendations.map(rec => (
                          <div 
                            key={rec.recommendation_id}
                            className={`p-3 border rounded-lg ${
                              rec.priority === 'urgent' ? 'border-red-300 bg-red-50' :
                              rec.priority === 'high' ? 'border-orange-300 bg-orange-50' :
                              'border-gray-200'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant={rec.priority === 'urgent' ? 'destructive' : 'secondary'}>
                                    {rec.priority}
                                  </Badge>
                                  <Badge variant="outline">{rec.category}</Badge>
                                </div>
                                <h4 className="font-medium">{rec.title}</h4>
                                <p className="text-sm text-muted-foreground mt-1">{rec.description}</p>
                                {rec.suggested_actions && (
                                  <ul className="mt-2 text-sm">
                                    {rec.suggested_actions.map((action, idx) => (
                                      <li key={idx} className="flex items-center gap-1">
                                        <ChevronRight className="w-3 h-3" />
                                        {action}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                              <Button 
                                size="sm" 
                                onClick={() => acceptRecommendation(rec)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                Accept
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No AI recommendations available
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Meeting Notes */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm">Meeting Notes</CardTitle>
                  </CardHeader>
                  <CardContent className="py-0 pb-3">
                    <Textarea
                      placeholder="Enter discussion notes for this patient..."
                      value={activePatient.notes}
                      onChange={(e) => updatePatientNotes(e.target.value)}
                      rows={4}
                    />
                    
                    {activePatient.decisions.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm font-medium mb-2">Decisions Made:</p>
                        <ul className="space-y-1">
                          {activePatient.decisions.map((decision, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm bg-green-50 p-2 rounded">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              {decision}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium mb-2">Select a Patient to Review</h3>
                  <p className="text-muted-foreground">
                    Click on a patient from the queue to begin their review
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Sidebar - Action Items */}
          <div className="col-span-3 space-y-4">
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Action Items ({actionItems.length})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="py-0 pb-3">
                {/* Add Action Item */}
                <div className="space-y-2 mb-4">
                  <Input
                    placeholder="New action item..."
                    value={newActionItem.text}
                    onChange={(e) => setNewActionItem(prev => ({ ...prev, text: e.target.value }))}
                  />
                  <div className="flex gap-2">
                    <Select 
                      value={newActionItem.assignee}
                      onValueChange={(val) => setNewActionItem(prev => ({ ...prev, assignee: val }))}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Assign to..." />
                      </SelectTrigger>
                      <SelectContent>
                        {attendees.map(a => (
                          <SelectItem key={a.id} value={a.role}>{a.role}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="date"
                      className="w-36"
                      value={newActionItem.dueDate}
                      onChange={(e) => setNewActionItem(prev => ({ ...prev, dueDate: e.target.value }))}
                    />
                  </div>
                  <Button onClick={addActionItem} className="w-full" size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Action Item
                  </Button>
                </div>

                {/* Action Items List */}
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {actionItems.map(item => (
                    <ActionItem
                      key={item.id}
                      item={item}
                      onUpdate={(updated) => {
                        setActionItems(prev => prev.map(i =>
                          i.id === item.id ? updated : i
                        ));
                      }}
                      onDelete={() => {
                        setActionItems(prev => prev.filter(i => i.id !== item.id));
                      }}
                    />
                  ))}
                  {actionItems.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No action items yet
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="py-0 pb-3 space-y-2">
                <Button variant="outline" className="w-full justify-start" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Export Minutes
                </Button>
                <Button variant="outline" className="w-full justify-start" size="sm">
                  <Send className="w-4 h-4 mr-2" />
                  Email Action Items
                </Button>
                <Button variant="outline" className="w-full justify-start" size="sm">
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule Follow-up
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
